import { useRef, useState } from "react";
import { uploadImageSigned } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, Loader2 } from "lucide-react";

/** Single-image upload with preview. onChange receives the secure URL string. */
export default function ImageUpload({ value, onChange, folder = "rotta/routes", label = "Imagem", testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Selecione uma imagem válida");
    if (file.size > 10 * 1024 * 1024) return toast.error("Imagem maior que 10 MB");
    setBusy(true);
    try {
      const { url } = await uploadImageSigned(file, folder);
      onChange(url);
      toast.success("Imagem enviada");
    } catch (e) {
      toast.error(e?.message || "Falha no upload", {
        description: "Se persistir, informe este código à equipe ROTTA.",
        duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762]">{label}</div>
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border border-[#2a2a2a]">
          <img src={value} alt="preview" className="w-full aspect-video object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-3">
            <button type="button" onClick={pick} className="btn-ghost px-4 py-2 rounded-full text-[11px] inline-flex items-center gap-2">
              <Upload size={12} /> Trocar
            </button>
            <button type="button" onClick={() => onChange("")} className="btn-ghost px-4 py-2 rounded-full text-[11px] inline-flex items-center gap-2 hover:text-red-400">
              <Trash2 size={12} /> Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="w-full aspect-video rounded-xl border-2 border-dashed border-[#2a2a2a] hover:border-[#C6A762]/50 hover:bg-white/[0.02] transition flex flex-col items-center justify-center gap-2 text-[#7d7d7d]"
        >
          {busy ? <Loader2 size={20} className="animate-spin text-[#C6A762]" /> : <ImageIcon size={22} />}
          <span className="text-xs">{busy ? "Enviando…" : "Clique para enviar"}</span>
          <span className="text-[10px] text-[#5a5a5a]">JPG, PNG, WEBP · máx 10 MB</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

/** Multi-image upload. onChange receives array of URLs. */
export function GalleryUpload({ value = [], onChange, folder = "rotta/gallery", label = "Galeria", testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const urls = [...value];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith("image/")) continue;
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: maior que 10 MB`); continue; }
      try {
        const { url } = await uploadImageSigned(f, folder);
        urls.push(url);
      } catch (e) { toast.error(`${f.name}: ${e?.message || "falha no envio"}`, { duration: 6000 }); }
    }
    onChange(urls);
    setBusy(false);
    toast.success("Galeria atualizada");
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#C6A762]">{label}</div>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-ghost px-3 py-1.5 rounded-full text-[10px] inline-flex items-center gap-2">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Adicionar
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={i} className="relative group rounded-lg overflow-hidden border border-[#2a2a2a] aspect-square">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(value.filter((_, k) => k !== i))}
              className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full bg-black/70 opacity-0 group-hover:opacity-100 transition text-white hover:text-red-400"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <div className="col-span-full text-xs text-[#7d7d7d] border border-dashed border-[#2a2a2a] rounded-lg p-8 text-center">
            Nenhuma imagem na galeria.
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handle(e.target.files)} />
    </div>
  );
}
